'use client';
import React, { useState, useRef, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import TwoFactorSetupModal from '@/components/TwoFactorSetupModal';

interface ProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'profile' | 'settings' | 'verification';
}

export default function ProfileSettingsModal({ isOpen, onClose, initialTab = 'profile' }: ProfileSettingsModalProps) {
  const { user } = useAuth();
  const supabase = createClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const frontDocRef = useRef<HTMLInputElement>(null);
  const backDocRef = useRef<HTMLInputElement>(null);
  const selfieRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'profile' | 'settings' | 'verification'>(initialTab);
  const [loading, setLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Profile fields
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');

  // Settings fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showOldPwModal, setShowOldPwModal] = useState(false);

  // Verification fields
  const [idDocType, setIdDocType] = useState('national_id');
  const [frontPreview, setFrontPreview] = useState('');
  const [backPreview, setBackPreview] = useState('');
  const [selfiePreview, setSelfiePreview] = useState('');
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [docUploading, setDocUploading] = useState(false);
  const [existingDocStatus, setExistingDocStatus] = useState<string>('none');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(false);
  // Phone OTP
  const [otpPhone, setOtpPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showTwoFASetup, setShowTwoFASetup] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isOpen && user) {
      loadProfile();
    }
  }, [isOpen, user]);

  const loadProfile = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('full_name, phone, avatar_url, phone_verified, identity_verified, id_document_status, totp_enabled, role')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      setFullName(data?.full_name || '');
      setPhone(data?.phone || '');
      setAvatarUrl(data?.avatar_url || '');
      setAvatarPreview(data?.avatar_url || '');
      setPhoneVerified(data?.phone_verified || false);
      setIdentityVerified(data?.identity_verified || false);
      setExistingDocStatus(data?.id_document_status || 'none');
      setTwoFAEnabled(data?.totp_enabled || false);
      setUserRole(data?.role || '');
      // Pre-fill OTP phone from profile
      const rawPhone = (data?.phone || '').replace('+254', '');
      setOtpPhone(rawPhone);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to load profile');
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    setAvatarUploading(true);
    setErrorMsg('');
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const publicUrl = urlData.publicUrl + `?t=${Date.now()}`;
      setAvatarUrl(publicUrl);
      setAvatarPreview(publicUrl);
      // Immediately update DB and notify app-wide
      await supabase.from('user_profiles').update({ avatar_url: publicUrl, updated_at: new Date().toISOString() }).eq('id', user.id);
      window.dispatchEvent(new CustomEvent('avatar-updated', { detail: publicUrl }));
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ full_name: fullName, phone: phone, avatar_url: avatarUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) throw error;
      // Notify app-wide of avatar change if it changed
      if (avatarUrl) {
        window.dispatchEvent(new CustomEvent('avatar-updated', { detail: avatarUrl }));
      }
      setSuccessMsg('Profile updated successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) { setErrorMsg('Please enter your current password first'); return; }
    if (!newPassword || !confirmPassword) { setErrorMsg('Please fill in all password fields'); return; }
    if (newPassword !== confirmPassword) { setErrorMsg('New passwords do not match'); return; }
    if (newPassword.length < 6) { setErrorMsg('Password must be at least 6 characters'); return; }
    if (currentPassword === newPassword) { setErrorMsg('New password must be different from current password'); return; }
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      // Verify current password first
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });
      if (signInError) {
        setErrorMsg('Current password is incorrect. Please try again.');
        setLoading(false);
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setSuccessMsg('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleFilePreview = (
    file: File,
    setPreview: (v: string) => void,
    setFile: (f: File) => void
  ) => {
    setFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const uploadDocFile = async (file: File, path: string): Promise<string> => {
    const { error } = await supabase.storage.from('id-documents').upload(path, file, { upsert: true });
    if (error) throw error;
    const { data } = supabase.storage.from('id-documents').getPublicUrl(path);
    return data.publicUrl;
  };

  const handleSubmitDocuments = async () => {
    if (!user) return;
    if (!frontFile) { setErrorMsg('Please upload the front of your ID document.'); return; }
    setDocUploading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const ts = Date.now();
      const frontUrl = await uploadDocFile(frontFile, `${user.id}/front_${ts}.${frontFile.name.split('.').pop()}`);
      let backUrl = '';
      let selfieUrl = '';
      if (backFile) {
        backUrl = await uploadDocFile(backFile, `${user.id}/back_${ts}.${backFile.name.split('.').pop()}`);
      }
      if (selfieFile) {
        selfieUrl = await uploadDocFile(selfieFile, `${user.id}/selfie_${ts}.${selfieFile.name.split('.').pop()}`);
      }

      // Upsert id_documents record
      const { error: docError } = await supabase.from('id_documents').upsert({
        user_id: user.id,
        document_type: idDocType,
        front_url: frontUrl,
        back_url: backUrl,
        selfie_url: selfieUrl,
        status: 'pending',
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' });
      if (docError) throw docError;

      // Update user_profiles doc status
      await supabase.from('user_profiles').update({
        id_document_type: idDocType,
        id_document_status: 'pending',
        updated_at: new Date().toISOString(),
      }).eq('id', user.id);

      setExistingDocStatus('pending');
      setSuccessMsg('Documents submitted for review! You will be notified once verified.');
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to upload documents');
    } finally {
      setDocUploading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!otpPhone || otpPhone.length < 9) { setOtpError('Enter a valid 9-digit phone number.'); return; }
    setOtpLoading(true);
    setOtpError('');
    setOtpSuccess('');
    try {
      // Generate a 6-digit OTP
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 min
      const fullPhone = `+254${otpPhone}`;

      // Store OTP in DB
      await supabase.from('phone_verifications').insert({
        user_id: user!.id,
        phone: fullPhone,
        otp_code: code,
        expires_at: expiresAt,
        attempts: 0,
      });

      // In production this would send via SMS gateway (Africa's Talking, etc.)
      // For demo: show OTP in success message
      setOtpSent(true);
      setOtpSuccess(`OTP sent to +254${otpPhone}. Demo code: ${code}`);
    } catch (err: any) {
      setOtpError(err.message || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) { setOtpError('Enter the 6-digit OTP code.'); return; }
    setOtpLoading(true);
    setOtpError('');
    try {
      const fullPhone = `+254${otpPhone}`;
      const now = new Date().toISOString();

      // Find matching OTP
      const { data: otpRecord, error: fetchErr } = await supabase
        .from('phone_verifications')
        .select('*')
        .eq('user_id', user!.id)
        .eq('phone', fullPhone)
        .eq('otp_code', otpCode)
        .is('verified_at', null)
        .gte('expires_at', now)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (fetchErr || !otpRecord) {
        setOtpError('Invalid or expired OTP. Please try again.');
        setOtpLoading(false);
        return;
      }

      // Mark OTP as verified
      await supabase.from('phone_verifications').update({ verified_at: now }).eq('id', otpRecord.id);

      // Update user profile
      await supabase.from('user_profiles').update({
        phone: fullPhone,
        phone_verified: true,
        updated_at: now,
      }).eq('id', user!.id);

      setPhoneVerified(true);
      setPhone(fullPhone);
      setOtpSuccess('Phone number verified successfully! ✓');
      setOtpSent(false);
      setOtpCode('');
    } catch (err: any) {
      setOtpError(err.message || 'Verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) { setDisableError('Enter the 6-digit code from your authenticator app.'); return; }
    setDisableLoading(true);
    setDisableError('');
    try {
      const res = await fetch('/api/2fa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: disableCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to disable 2FA');
      setTwoFAEnabled(false);
      setShowDisableConfirm(false);
      setDisableCode('');
      setSuccessMsg('Two-factor authentication has been disabled.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err: any) {
      setDisableError(err.message || 'Failed to disable 2FA');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleClose = () => {
    setErrorMsg('');
    setSuccessMsg('');
    onClose();
  };

  if (!isOpen) return null;

  const docStatusConfig: Record<string, { label: string; color: string; icon: string }> = {
    none: { label: 'Not Submitted', color: 'text-muted-foreground', icon: '○' },
    pending: { label: 'Under Review', color: 'text-warning', icon: '⏳' },
    approved: { label: 'Verified', color: 'text-success', icon: '✓' },
    rejected: { label: 'Rejected', color: 'text-danger', icon: '✗' },
  };
  const docStatus = docStatusConfig[existingDocStatus] || docStatusConfig['none'];

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={handleClose} />
        <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Account</h2>
            <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            {(['profile', 'verification', 'settings'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setErrorMsg(''); setSuccessMsg(''); }}
                className={`flex-1 py-3 text-xs font-medium capitalize transition-colors relative ${
                  activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'verification' ? 'Verify ID' : tab}
                {tab === 'verification' && identityVerified && (
                  <span className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-success text-white text-[8px]">✓</span>
                )}
                {tab === 'verification' && phoneVerified && !identityVerified && (
                  <span className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-info text-white text-[8px]">✓</span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-4 max-h-[72vh] overflow-y-auto">
            {/* Feedback messages */}
            {successMsg && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 text-sm">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {successMsg}
              </div>
            )}
            {errorMsg && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {errorMsg}
              </div>
            )}

            {activeTab === 'profile' && (
              <>
                {/* Avatar */}
                <div className="flex flex-col items-center gap-3 pb-2">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden border-2 border-border">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Profile avatar" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                    </div>
                    {avatarUploading && (
                      <div className="absolute inset-0 rounded-full bg-foreground/40 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleAvatarChange} />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={avatarUploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted hover:bg-muted/80 text-foreground transition-colors disabled:opacity-50"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {avatarUploading ? 'Uploading…' : 'Change Photo'}
                  </button>
                  <p className="text-xs text-muted-foreground">JPG, PNG, WebP or GIF · Max 5 MB</p>
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your full name"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
                </div>

                {/* Email (read-only) */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email</label>
                  <input type="email" value={user?.email || ''} readOnly
                    className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground cursor-not-allowed" />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    Phone Number
                    {phoneVerified && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-600">
                        <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        Verified
                      </span>
                    )}
                  </label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+254 7XX XXX XXX"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
                  {!phoneVerified && (
                    <button onClick={() => { setActiveTab('verification'); }} className="mt-1.5 text-xs text-primary hover:underline">
                      → Verify your phone number
                    </button>
                  )}
                </div>

                <button onClick={handleSaveProfile} disabled={loading || avatarUploading}
                  className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? (
                    <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Saving…</>
                  ) : 'Save Profile'}
                </button>
              </>
            )}

            {activeTab === 'verification' && (
              <div className="space-y-5">
                {/* Verification status overview */}
                <div className="grid grid-cols-2 gap-3">
                  <div className={`rounded-xl border p-3 text-center ${phoneVerified ? 'border-success/30 bg-success/5' : 'border-border bg-muted/30'}`}>
                    <div className={`text-xl mb-1 ${phoneVerified ? 'text-success' : 'text-muted-foreground'}`}>
                      {phoneVerified ? '✓' : '○'}
                    </div>
                    <p className="text-xs font-600 text-foreground">Phone</p>
                    <p className={`text-[10px] mt-0.5 ${phoneVerified ? 'text-success' : 'text-muted-foreground'}`}>
                      {phoneVerified ? 'Verified' : 'Not verified'}
                    </p>
                  </div>
                  <div className={`rounded-xl border p-3 text-center ${identityVerified ? 'border-success/30 bg-success/5' : existingDocStatus === 'pending' ? 'border-warning/30 bg-warning/5' : 'border-border bg-muted/30'}`}>
                    <div className={`text-xl mb-1 ${identityVerified ? 'text-success' : existingDocStatus === 'pending' ? 'text-warning' : 'text-muted-foreground'}`}>
                      {identityVerified ? '✓' : existingDocStatus === 'pending' ? '⏳' : '○'}
                    </div>
                    <p className="text-xs font-600 text-foreground">Identity</p>
                    <p className={`text-[10px] mt-0.5 ${docStatus.color}`}>{docStatus.label}</p>
                  </div>
                </div>

                {/* Identity badge preview */}
                {identityVerified && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-success/10 border border-success/20">
                    <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <p className="text-xs font-700 text-success">Identity Confirmed</p>
                      <p className="text-[10px] text-muted-foreground">Your badge is visible to senders on trip cards</p>
                    </div>
                  </div>
                )}

                {/* ── PHONE VERIFICATION ── */}
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <p className="text-sm font-600 text-foreground">Phone Verification</p>
                    </div>
                    {phoneVerified && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-600">
                        <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    {phoneVerified ? (
                      <p className="text-sm text-success font-500">Your phone number is verified ✓</p>
                    ) : (
                      <>
                        {otpError && <p className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">{otpError}</p>}
                        {otpSuccess && <p className="text-xs text-success bg-success/5 border border-success/20 rounded-lg px-3 py-2">{otpSuccess}</p>}
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Phone Number</label>
                          <div className="flex gap-2">
                            <div className="flex items-center gap-1 px-3 py-2 rounded-lg border border-border bg-muted text-xs text-muted-foreground flex-shrink-0">
                              <span>🇰🇪</span><span className="font-500">+254</span>
                            </div>
                            <input
                              type="tel"
                              value={otpPhone}
                              onChange={e => setOtpPhone(e.target.value.replace(/\D/g, '').slice(0, 9))}
                              disabled={otpSent}
                              placeholder="712 345 678"
                              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-muted disabled:text-muted-foreground"
                            />
                          </div>
                        </div>
                        {!otpSent ? (
                          <button onClick={handleSendOtp} disabled={otpLoading || otpPhone.length < 9}
                            className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                            {otpLoading ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Sending…</> : 'Send OTP Code'}
                          </button>
                        ) : (
                          <div className="space-y-2">
                            <label className="block text-xs font-500 text-muted-foreground">Enter 6-digit OTP</label>
                            <input
                              type="text"
                              value={otpCode}
                              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="123456"
                              maxLength={6}
                              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                            />
                            <div className="flex gap-2">
                              <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpError(''); setOtpSuccess(''); }}
                                className="flex-1 py-2 rounded-lg border border-border text-sm font-500 hover:bg-muted transition-colors">
                                Resend
                              </button>
                              <button onClick={handleVerifyOtp} disabled={otpLoading || otpCode.length !== 6}
                                className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                                {otpLoading ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> : null}
                                Verify
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ── ID DOCUMENT UPLOAD ── */}
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                      </svg>
                      <p className="text-sm font-600 text-foreground">ID Document Upload</p>
                    </div>
                    <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${
                      existingDocStatus === 'approved' ? 'bg-success/10 text-success' :
                      existingDocStatus === 'pending' ? 'bg-warning/10 text-warning' :
                      existingDocStatus === 'rejected'? 'bg-danger/10 text-danger' : 'bg-muted text-muted-foreground'
                    }`}>{docStatus.icon} {docStatus.label}</span>
                  </div>
                  <div className="p-4 space-y-4">
                    {existingDocStatus === 'approved' ? (
                      <div className="text-center py-4">
                        <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-2">
                          <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <p className="text-sm font-600 text-success">Identity Verified</p>
                        <p className="text-xs text-muted-foreground mt-1">Your identity badge is now visible to senders</p>
                      </div>
                    ) : existingDocStatus === 'pending' ? (
                      <div className="flex items-start gap-3 p-3 bg-warning/5 border border-warning/20 rounded-xl">
                        <span className="text-warning text-lg flex-shrink-0">⏳</span>
                        <div>
                          <p className="text-sm font-600 text-foreground">Documents Under Review</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Our team is reviewing your submitted documents. This usually takes 1–2 business days.</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Document type */}
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Document Type</label>
                          <select value={idDocType} onChange={e => setIdDocType(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                            <option value="national_id">National ID Card</option>
                            <option value="passport">Passport</option>
                            <option value="driving_license">Driving License</option>
                            <option value="alien_id">Alien ID</option>
                          </select>
                        </div>

                        {/* Front upload */}
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Front of Document <span className="text-danger">*</span></label>
                          <input ref={frontDocRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleFilePreview(f, setFrontPreview, setFrontFile); }} />
                          {frontPreview ? (
                            <div className="relative rounded-lg overflow-hidden border border-border">
                              <img src={frontPreview} alt="Front of ID document" className="w-full h-28 object-cover" />
                              <button onClick={() => { setFrontPreview(''); setFrontFile(null); }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-foreground/60 text-white flex items-center justify-center text-xs hover:bg-foreground/80">✕</button>
                            </div>
                          ) : (
                            <button onClick={() => frontDocRef.current?.click()}
                              className="w-full h-24 rounded-lg border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 flex flex-col items-center justify-center gap-1.5 transition-colors">
                              <svg className="w-6 h-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs text-muted-foreground">Click to upload front</span>
                            </button>
                          )}
                        </div>

                        {/* Back upload */}
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Back of Document <span className="text-muted-foreground text-[10px]">(optional for passport)</span></label>
                          <input ref={backDocRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleFilePreview(f, setBackPreview, setBackFile); }} />
                          {backPreview ? (
                            <div className="relative rounded-lg overflow-hidden border border-border">
                              <img src={backPreview} alt="Back of ID document" className="w-full h-24 object-cover" />
                              <button onClick={() => { setBackPreview(''); setBackFile(null); }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-foreground/60 text-white flex items-center justify-center text-xs hover:bg-foreground/80">✕</button>
                            </div>
                          ) : (
                            <button onClick={() => backDocRef.current?.click()}
                              className="w-full h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 flex flex-col items-center justify-center gap-1 transition-colors">
                              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs text-muted-foreground">Click to upload back</span>
                            </button>
                          )}
                        </div>

                        {/* Selfie upload */}
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Selfie with Document <span className="text-[10px] text-muted-foreground">(recommended)</span></label>
                          <input ref={selfieRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleFilePreview(f, setSelfiePreview, setSelfieFile); }} />
                          {selfiePreview ? (
                            <div className="relative rounded-lg overflow-hidden border border-border">
                              <img src={selfiePreview} alt="Selfie with ID document" className="w-full h-24 object-cover" />
                              <button onClick={() => { setSelfiePreview(''); setSelfieFile(null); }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-foreground/60 text-white flex items-center justify-center text-xs hover:bg-foreground/80">✕</button>
                            </div>
                          ) : (
                            <button onClick={() => selfieRef.current?.click()}
                              className="w-full h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 flex flex-col items-center justify-center gap-1 transition-colors">
                              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="text-xs text-muted-foreground">Click to upload selfie</span>
                            </button>
                          )}
                        </div>

                        <div className="flex items-start gap-2 p-2.5 bg-info/5 border border-info/20 rounded-lg">
                          <svg className="w-3.5 h-3.5 text-info flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-[10px] text-muted-foreground leading-relaxed">
                            Documents are encrypted and stored securely. Only used for identity verification. Max 100MB per file.
                          </p>
                        </div>

                        <button onClick={handleSubmitDocuments} disabled={docUploading || !frontFile}
                          className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                          {docUploading ? (
                            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Uploading…</>
                          ) : (
                            <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>Submit for Verification</>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <>
                <div className="pb-1">
                  <h3 className="text-sm font-semibold text-foreground">Change Password</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Update your account password</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Current Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type={showOldPwModal ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password"
                      className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
                    <button type="button" onClick={() => setShowOldPwModal(!showOldPwModal)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showOldPwModal ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">New Password</label>
                  <div className="relative">
                    <input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 6 characters"
                      className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showNewPw ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm New Password</label>
                  <div className="relative">
                    <input type={showCurrentPw ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat new password"
                      className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
                    <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showCurrentPw ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      )}
                    </button>
                  </div>
                </div>

                <button onClick={handleChangePassword} disabled={loading}
                  className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? (
                    <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Updating…</>
                  ) : 'Update Password'}
                </button>

                {/* ── TWO-FACTOR AUTHENTICATION ── */}
                {['admin', 'station_moderator', 'sender'].includes(userRole) && (
                  <div className="border border-border rounded-xl overflow-hidden mt-2">
                    <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <p className="text-sm font-600 text-foreground">Two-Factor Authentication</p>
                      </div>
                      <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${twoFAEnabled ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                        {twoFAEnabled ? '✓ Enabled' : 'Off'}
                      </span>
                    </div>
                    <div className="p-4 space-y-3">
                      {twoFAEnabled ? (
                        <>
                          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-success/5 border border-success/20">
                            <svg className="w-4 h-4 text-success flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <p className="text-xs text-muted-foreground">Your account is protected with an authenticator app. A code is required on every sign-in.</p>
                          </div>
                          {!showDisableConfirm ? (
                            <button
                              onClick={() => { setShowDisableConfirm(true); setDisableError(''); setDisableCode(''); }}
                              className="w-full py-2 rounded-lg border border-danger/30 text-danger text-sm font-medium hover:bg-danger/5 transition-colors"
                            >
                              Disable 2FA
                            </button>
                          ) : (
                            <div className="space-y-2.5 p-3 rounded-lg bg-danger/5 border border-danger/20">
                              <p className="text-xs font-medium text-foreground">Enter your current authenticator code to confirm:</p>
                              {disableError && <p className="text-xs text-danger">{disableError}</p>}
                              <input
                                type="text"
                                inputMode="numeric"
                                value={disableCode}
                                onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                maxLength={6}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-danger/30 focus:border-danger transition-colors"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => { setShowDisableConfirm(false); setDisableCode(''); setDisableError(''); }}
                                  className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleDisable2FA}
                                  disabled={disableLoading || disableCode.length !== 6}
                                  className="flex-1 py-2 rounded-lg bg-danger text-white text-sm font-semibold hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                                >
                                  {disableLoading ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> : null}
                                  Confirm Disable
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-muted-foreground">
                            Add an extra layer of security to your account. You'll need an authenticator app (Google Authenticator, Authy, etc.) to sign in.
                          </p>
                          <button
                            onClick={() => setShowTwoFASetup(true)}
                            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Enable Two-Factor Auth
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <TwoFactorSetupModal
        isOpen={showTwoFASetup}
        onClose={() => setShowTwoFASetup(false)}
        onEnabled={() => { setTwoFAEnabled(true); setShowTwoFASetup(false); }}
      />
    </>
  );
}
